import React from "react";
import StatusBadge from "../common/StatusBadge";
import { ReadOnlyToggle, ScheduleActionsPanel } from "./ScheduleBits";
import {
  formatDateTime,
  formatZman,
  formatPause,
  formatRecurrence,
  formatBool,
  dash,
} from "./scheduleFormat";

/**
 * The schedule detail content, with no chrome of its own — rendered both inside
 * the Detail modal and on the /schedules/:id route, so the two can never drift.
 */
function Field({ label, children }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
        {label}
      </p>
      <div className="mt-1 text-sm text-slate-800">{children}</div>
    </div>
  );
}

export function ScheduleDetailBadges({ schedule }) {
  return (
    <>
      <StatusBadge label="Status" kind="scheduleStatus" value={schedule.status} size="md" />
      <StatusBadge label="Run" kind="cronStatus" value={schedule.cronstatus} size="md" />
      {schedule.signal && (
        <StatusBadge label="Plan" kind="signal" value={schedule.signal} size="md" />
      )}
      <ReadOnlyToggle status={schedule.status} />
    </>
  );
}

export default function ScheduleDetailBody({ schedule, deviceIndex }) {
  if (!schedule) return null;

  const when = schedule.when || {};
  const pauseText = formatPause(schedule.pauseSummary ?? schedule.pause);

  return (
    <div>
      {pauseText && (
        <p className="mb-5 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {pauseText}
        </p>
      )}

      <div className="grid grid-cols-2 gap-5 md:grid-cols-4">
        <Field label="Recurrence">{formatRecurrence(schedule)}</Field>
        <Field label="Time">{formatZman(when.zmanim) || dash(when.exactTime)}</Field>
        <Field label="Timezone">{dash(when.timezone)}</Field>
        <Field label="Calendar">{dash(when.calenderType)}</Field>

        <Field label="Next run">
          {/* A draft has no computed next run — say so rather than "—",
              which reads as "we don't know". */}
          {schedule.status === "draft"
            ? "Not scheduled"
            : formatDateTime(schedule.nextRunAt ?? when.nextRunAt)}
        </Field>
        <Field label="Round to closest">{formatBool(schedule.roundtoclosest)}</Field>
        <Field label="Devices">{dash(schedule.deviceSummary)}</Field>
        <Field label="Rooms">
          {/* formatSchedulesForUI sends the literal string "room" when the
              schedule's devices belong to no room — that is not a room name. */}
          {dash(schedule.roomSummary === "room" ? null : schedule.roomSummary)}
        </Field>
      </div>

      {/* zmanimInfo is the backend's own human description — show it so admin
          and the app agree on what the zman means. */}
      {when.zmanimInfo?.title && (
        <p className="mt-5 rounded-lg bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
          <span className="font-semibold">Fires:</span> {when.zmanimInfo.title}
        </p>
      )}

      {schedule.resyncRequired && (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          This schedule is flagged as needing a device re-sync.
        </p>
      )}

      <h3 className="mb-3 mt-7 text-xs font-bold uppercase tracking-wide text-slate-500">
        Devices &amp; actions
      </h3>
      <ScheduleActionsPanel schedule={schedule} deviceIndex={deviceIndex} />
    </div>
  );
}
